import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ClipboardList, Users, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, STOCK_LABEL, fmtPrice } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { ProductCard } from "@/components/ProductCard";

export default function BrandPage() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const { add, setOpen } = useQuote();
  useEffect(() => { setB(null); api.get(`/brands/${slug}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [slug]);

  if (b === false) return <div className="py-24 text-center text-slate-500">Brand not found.</div>;
  if (!b) return <div className="py-24 text-center text-slate-400">Loading…</div>;

  const addAll = () => { b.products.forEach((p) => add(p, 1)); toast.success(`Added ${b.products.length} ${b.name} SKUs to your quote list`); setOpen(true); };

  return (
    <div data-testid="brand-page">
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.accent}14, #ffffff 60%)` }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8">
          <Link to="/brands" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1E7CF2]" data-testid="back-to-brands"><ArrowLeft size={14} /> All brands</Link>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="sr-label sr-red-rule">Brand collection · {b.category}</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-[#0B1F4B]" data-testid="brand-name">{b.name}</h1>
            <p className="mt-3 text-lg font-semibold uppercase" style={{ color: b.accent }}>{b.tagline}</p>
            <p className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed">{b.description}</p>
            {b.motto && <p className="mt-4 italic text-[#0B1F4B] font-display text-xl">"{b.motto}"</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={addAll} className="sr-btn-primary" data-testid="brand-request-quote-all"><ClipboardList size={16} /> Request quote for full line</button>
              <a href={`${API}/brands/${b.slug}/sell-sheet.pdf`} target="_blank" rel="noreferrer" className="sr-btn-outline" data-testid="brand-sell-sheet-download"><FileDown size={16} /> Download sell sheet (PDF)</a>
            </div>
          </div>
          <div className="lg:col-span-7"><img src={b.image} alt={b.name} className="w-full rounded-3xl object-cover shadow-2xl shadow-[#0B1F4B]/15" /></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 sr-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200"><h2 className="text-xl font-extrabold uppercase text-[#0B1F4B]">Product line</h2></div>
          <table className="w-full text-sm" data-testid="brand-product-table">
            <thead className="bg-[#EBF3FE] text-[#1E40AF] text-[11px] uppercase tracking-wider"><tr><th className="text-left px-6 py-2.5">Product</th><th className="text-left px-3 py-2.5">Size</th><th className="text-left px-3 py-2.5 hidden sm:table-cell">Profile</th><th className="text-left px-3 py-2.5">Case</th><th className="text-right px-6 py-2.5">Price</th></tr></thead>
            <tbody>
              {b.products.map((p) => (
                <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-3 font-semibold text-[#0B1F4B]"><Link to={`/products/${p.product_id}`} className="hover:text-[#1E7CF2]" data-testid={`brand-table-link-${p.product_id}`}>{p.name}</Link><p className="text-[11px] text-slate-400 font-normal">{STOCK_LABEL[p.stock_status]}</p></td>
                  <td className="px-3 py-3 text-slate-600">{p.size}</td>
                  <td className="px-3 py-3 text-slate-600 hidden sm:table-cell">{p.flavor_profile || "—"}</td>
                  <td className="px-3 py-3 font-mono text-slate-600">{p.case_pack || "TBC"}</td>
                  <td className="px-6 py-3 text-right text-xs font-semibold text-[#1E7CF2] uppercase">{p.price_on_request ? "On request" : fmtPrice(p.display_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="lg:col-span-5 space-y-5">
          <div className="sr-card-tint p-6">
            <h3 className="font-bold uppercase text-[#0B1F4B] flex items-center gap-2"><CheckCircle2 size={18} className="text-[#1E7CF2]" /> Why it sells</h3>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-700">{b.why_it_sells.map((w) => <li key={w} className="flex gap-2"><span className="text-[#1E7CF2]">•</span>{w}</li>)}</ul>
          </div>
          <div className="sr-card-tint p-6">
            <h3 className="font-bold uppercase text-[#0B1F4B] flex items-center gap-2"><Users size={18} className="text-[#1E7CF2]" /> Ideal buyers</h3>
            <div className="mt-3 flex flex-wrap gap-2">{b.ideal_buyers.map((x) => <span key={x} className="text-xs rounded-full bg-white border border-[#D6E6FB] px-3 py-1 text-[#1E40AF] font-medium">{x}</span>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {b.highlights.map((h) => <div key={h} className="sr-card p-4 text-center text-xs font-bold uppercase tracking-wider text-[#0B1F4B]">{h}</div>)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-20">
        <h2 className="text-2xl font-extrabold uppercase text-[#0B1F4B]">All {b.name} SKUs</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{b.products.map((p) => <ProductCard key={p.product_id} p={p} />)}</div>
      </section>
    </div>
  );
}
